/**
 * SponsoredSlot - the orchestrator dropped into a page surface.
 *
 * Usage:
 *   <SponsoredSlot
 *     surface="suburb-glenelg"
 *     topics={['suburb', 'first-home', 'custom-design']}
 *     format="infeed"
 *   />
 *
 * Behaviour:
 *   - Hidden entirely for premium users (no DOM)
 *   - Resolves to a SponsoredCard if a live ad matches the surface
 *     state + topic filter (state-required; ads without state never
 *     show)
 *   - Falls back to AdvertiseHereCard placeholder if no match - the
 *     CTA opens the AdvertisingEnquiryModal so empty inventory still
 *     drives leads
 *   - Re-resolves when topics/state change so SPA navigation works
 */
import React, { useEffect, useState } from 'react';
import { useSubscription } from '../../workspace/hooks/useSubscription';
import { getRandomAdvert } from '../../workspace/services/advertisingService';
import { getViewerState } from './adContext';
import SponsoredCard from './SponsoredCard';
import AdvertiseHereCard from './AdvertiseHereCard';

const adMatchesTopics = (ad, topics) => {
  if (!topics || topics.length === 0) return true;
  // target_topics is optional on the advert. Empty/null means the ad
  // is willing to appear anywhere within its state targeting, so we
  // accept it. Otherwise require at least one topic overlap.
  const adTopics = Array.isArray(ad?.target_topics) ? ad.target_topics : null;
  if (!adTopics || adTopics.length === 0) return true;
  return adTopics.some((t) => topics.includes(t));
};

const SponsoredSlot = ({
  surface,
  topics = [],
  // 'small' (800x150), 'medium' (800x250), 'square' (1080x1080), 'large' (9:16)
  size = 'small',
  // Legacy alias (`format` was the original prop name)
  format,
  className = '',
  hideIfEmpty = false,
}) => {
  const resolvedSize = format || size;
  const { isPremium, loading: subLoading } = useSubscription();
  const [ad, setAd] = useState(null);
  const [resolved, setResolved] = useState(false);
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const viewerState = await getViewerState();
      if (cancelled) return;
      setState(viewerState);

      // Note: a missing viewer state is FINE. Anonymous + no-state
      // logged-in users still see ads. The advertisingService falls
      // back to all-states inventory in that case. The advertiser-
      // side rule (must have at least one state target) still
      // applies inside getAdverts(), so ads without state never
      // serve regardless of who's viewing.

      // Try up to 6 picks to find a topic-matching ad. The underlying
      // advertisingService already handles state filtering + no-repeat
      // rotation; we only add the topic intersection filter on top.
      let pick = null;
      for (let i = 0; i < 6; i += 1) {
        const candidate = await getRandomAdvert(surface);
        if (!candidate) break;
        if (adMatchesTopics(candidate, topics)) {
          pick = candidate;
          break;
        }
      }
      if (cancelled) return;
      setAd(pick);
      setResolved(true);
    })();
    return () => { cancelled = true; };
  }, [surface, JSON.stringify(topics)]);

  if (subLoading) return null;
  if (isPremium) return null;
  if (!resolved) return null;

  if (ad) {
    return (
      <div className={className} data-testid={`sponsored-slot-${surface}`}>
        <SponsoredCard ad={ad} surface={surface} size={resolvedSize} state={state} />
      </div>
    );
  }

  if (hideIfEmpty) return null;

  return (
    <div className={className} data-testid={`sponsored-slot-${surface}-empty`}>
      <AdvertiseHereCard surface={surface} state={state} size={resolvedSize} />
    </div>
  );
};

export default SponsoredSlot;
